import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { IOrder, IOrderAttributesItems, IOrderById, IOrderData } from '../../../models/order.models';
import { OrdersFacade } from '../../../facades/orders.facade';
import { MarketingFacade } from '../../../facades/marketing.facade';
import { ArrayUtils } from '../../../utils/array.utils';
import { Subject } from 'rxjs';
import { CheckoutFacade } from '../../../facades/checkout.facade';
import { IRfqBaseInfo } from '../../../models/rfq.models';
import { takeUntil } from 'rxjs/operators';
import { AppUtils } from '../../../utils/app.utils';
import { environment } from '../../../../environments/environment';
import { IStore } from '../../../models/settings.model';

@Component({
  selector: 'app-my-order-hist-detail',
  templateUrl: './my-order-hist-detail.component.html',
  styleUrls: ['./my-order-hist-detail.component.scss'],
  standalone: false,
})
export class MyOrderHistDetailComponent implements OnInit, OnChanges, OnDestroy {
  private ordersFacade: OrdersFacade = inject(OrdersFacade);
  private marketingFacade: MarketingFacade = inject(MarketingFacade);
  private checkoutFacade: CheckoutFacade = inject(CheckoutFacade);

  orderDetails!: IOrder | IRfqBaseInfo | IOrderData;
  cartDetails!: any;
  cartItems: string[] = [];
  itemsLoaded: boolean = false;
  itemsInQueueToCart!: Array<string>;

  private unsubscribe$: Subject<void> = new Subject<void>();

  @Input() order!: any;
  @Input() isOpen!: boolean;
  @Input() type: string = 'order';

  ngOnInit(): void {
    this.selectItemsInQueueToCart();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isOpen.currentValue && !this.itemsLoaded) {
      if (this.order.type === 'orders') {
        this.retrieveOrderDetails(this.order.id);
      } else if (this.order.type === 'quote-requests') {
        this.retrieveRequestQuoteDetails(this.order.id);
      } else {
        this.retrieveCartOrderDetails(this.order.id);
      }
    }
  }

  combineSameItems(order: IOrderData): IOrderAttributesItems[] {
    const items: IOrderAttributesItems[] = [...order.attributes.items];
    return ArrayUtils.uniqueObjects(items, 'sku');
  }

  retrieveCartOrderDetails(id: string): void {
    this.ordersFacade.getCartForApprovalData(id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(data => {
        this.itemsLoaded = true;
        if (data) {
          this.cartDetails = data;
          this.cartDetails.data.relationships.items.data.forEach((item: any) => {
            if (this.cartItems.indexOf(item.id) === -1) {
              this.cartItems.push(item.id);
            }
          });
          this.orderDetails = this.cartDetails.data;
        }
      });
  }

  retrieveOrderDetails(id: string): void {
    this.ordersFacade.getOrderById(id).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe((data: IOrderById) => {
      this.itemsLoaded = true;
      if (data) {
        const orderDetails: IOrderData = data.data;
        orderDetails.attributes.items = this.combineSameItems(orderDetails);
        this.orderDetails = orderDetails;
      }
    });
  }

  retrieveRequestQuoteDetails(id: string): void {
    this.checkoutFacade.selectQuoteDetails(id).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(data => {
      this.itemsLoaded = true;
      if (data) {
        this.orderDetails = data.data;
      }
    });
  }

  addProductToCart(product: string, productName: string): void {
    this.marketingFacade.addProductToCart({sku: product, name: productName}, true);
  }

  getItemPrice(id: string): number {
    return this.cartDetails.included?.filter((tmp: any) => tmp.type === 'items' && tmp.id === id)[0].attributes.calculations.unitPrice;
  }

  getItemQuantity(id: string): number {
    return this.cartDetails.included?.filter((tmp: any) => tmp.type === 'items' && tmp.id === id)[0].attributes.quantity;
  }

  getItemName(id: string): string {
    return this.cartDetails.included?.filter((tmp: any) => tmp.type === 'concrete-products' && tmp.id === id)[0].attributes.name;
  }

  getItemPicture(id: string): string {
    return this.cartDetails.included?.filter((tmp: any) => tmp.type === 'concrete-product-image-sets' && tmp.id === id)[0]
      .attributes.imageSets.find(
        (img: any) => img.name === 'default')
      .images.find((smallImage: any) => smallImage.externalUrlSmall)
      .externalUrlSmall;
  }

  renderEmptyAddressField(field: string): string {
    return field === 'empty' ? '' : field;
  }

  isAddressFieldEmpty(field: string): boolean {
    return field === 'empty' || field === '' || !field;
  }

  selectItemsInQueueToCart() {
    this.marketingFacade.selectItemsInQueueToCart()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(state => {
        this.itemsInQueueToCart = state;
      });
  }

  isItemInProgress(sku: string): boolean {
    return this.itemsInQueueToCart.includes(sku);
  }

  buyButtonLabel(): string {
    return this.type === 'order' ? 'orders-page.buy-again' : 'orders-page.request-again';
  }

  isExcludeTaxActive(): boolean {
    const currentStore: IStore = AppUtils.getCurrentStore();
    return environment.features.excludeTax.includes(currentStore.storeId);
  }
}
